import { useCalendar } from "~/store/calendar";
import { useMasters } from "~/store/masters";

/**
 * Formatea una fecha a un formato específico (DD/MM/YYYY)
 * @param {Date} date - Fecha a formatear
 * @param {String} format - Formato de salida (opcional)
 * @param {String} separator - Separador entre los componentes de la fecha (opcional)
 * @returns {String} - Fecha formateada en el formato especificado
 * @example
 * formatDate(new Date(), "DMY", "-") // "23-08-2021"
 * formatDate(new Date(), "MDY", "/") // "08/23/2021"
 * formatDate(new Date(), "YMD", "-") // "2021-08-23"
 * formatDate(new Date(), "iso") // "2021-08-23"
 * formatDate(new Date(), "complete_es") // "23/08/2021 12:00:00"
 * formatDate(new Date(), "complete_en") // "08/23/2021 12:00:00"
 * formatDate(new Date(), "day") // "23"
 * formatDate(new Date(), "month") // "08"
 * formatDate(new Date(), "monthName") // "August"
 * formatDate(new Date(), "year") // "2021"
 */
export const formatDate = (date, format = null, separator = "/") => {
  const nuxtApp = useNuxtApp();

  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();

  const formated_day = day < 10 ? "0" + day : day;
  const formated_month = month < 10 ? "0" + month : month;
  const formated_hours = hours > 0 && hours < 10 ? "0" + hours : hours;
  const formated_minutes =
    minutes > 0 && minutes < 10 ? "0" + minutes : minutes;
  const formated_seconds =
    seconds > 0 && seconds < 10 ? "0" + seconds : seconds;

  if (format) {
    switch (format) {
      case "day":
        return formated_day;
      case "month":
        return formated_month;
      case "monthName":
        return nuxtApp.$i18n.t(`common.calendar.months.${month}`);
      case "year":
        return year;
      case "complete_es":
        return `${formated_day}/${formated_month}/${year} ${formated_hours}:${formated_minutes}:${formated_seconds}`;
      case "complete_en":
        return `${formated_month}/${formated_day}/${year} ${formated_hours}:${formated_minutes}:${formated_seconds}`;
      case "minutes_es":
        return `${formated_day}/${formated_month}/${year} ${formated_hours}:${formated_minutes}`;
      case "minutes_en":
        return `${formated_month}/${formated_day}/${year} ${formated_hours}:${formated_minutes}`;
      case "DMY":
        return `${formated_day}${separator}${formated_month}${separator}${year}`;
      case "MDY":
        return `${formated_month}${separator}${formated_day}${separator}${year}`;
      case "YMD":
        return `${year}${separator}${formated_month}${separator}${formated_day}`;
      case "iso":
        // return date.toISOString().slice(0, 10);
        return (
          date.getFullYear() +
          "-" +
          String(date.getMonth() + 1).padStart(2, "0") +
          "-" +
          String(date.getDate()).padStart(2, "0")
        );
    }
  }

  return nuxtApp.$i18n.locale.value === "es"
    ? `${formated_day}/${formated_month}/${year}`
    : `${formated_month}/${formated_day}/${year}`;
};

/**
 * Obtiene el número de semana del año según ISO 8601 (calendario español/europeo)
 * @param {Date} date
 * @returns {Number} - El número de semana del año (1-53)
 */
export const getWeek = (date) => {
  const copiedDate = new Date(date.getTime());
  copiedDate.setHours(0, 0, 0, 0);
  
  // Ajustar al jueves de la misma semana (el jueves determina la semana en ISO 8601)
  // getDay() devuelve 0 para domingo, lo convertimos a 7 para facilitar el cálculo
  const dayOfWeek = copiedDate.getDay() || 7;
  copiedDate.setDate(copiedDate.getDate() + 4 - dayOfWeek);
  
  // Obtener el 1 de enero del año del jueves (que podría ser diferente al año de la fecha original)
  const yearStart = new Date(copiedDate.getFullYear(), 0, 1);
  
  // Calcular el número de semanas completas desde el 1 de enero hasta el jueves
  const weekNumber = Math.ceil(((copiedDate - yearStart) / 86400000 + 1) / 7);
  
  return weekNumber;
};

/**
 * Confirma si la fecha está entre la semana actual y la siguiente
 * @param {String} date
 * @returns     {boolean}
 */
export const isCurrentOrNextWeek = (date) => {
  const start_range = getStartOfWeek(parseDateAsUTC());
  const end_range = addDays(start_range, 13);

  return (
    normalizeDate(parseDateAsUTC(date)) >= normalizeDate(start_range) &&
    normalizeDate(parseDateAsUTC(date)) <= normalizeDate(end_range)
  );
};

/**
 * Confirma si la fecha está en la semana actual
 * @param {String} date
 * @returns  {boolean}
 */
export const isCurrentWeek = (date) => {
  const start_range = getStartOfWeek(parseDateAsUTC());
  const end_range = addDays(start_range, 6);

  return (
    normalizeDate(parseDateAsUTC(date)) >= normalizeDate(start_range) &&
    normalizeDate(parseDateAsUTC(date)) <= normalizeDate(end_range)
  );
};

/**
 * Confirma si la fecha está en la semana actual
 * @param {String} date
 * @returns {boolean}
 */
export const isCurrentWeekOrBefore = (date) => {
  const sunday = addDays(getStartOfWeek(parseDateAsUTC()), 6);

  return normalizeDate(parseDateAsUTC(date)) <= normalizeDate(sunday);
};

/**
 * Confirma si la fecha está en la semana actual
 * @param {String} date
 * @returns {boolean}
 */
export const isBeforeToday = (date) => {
  const today = parseDateAsUTC();

  return normalizeDate(parseDateAsUTC(date)) < normalizeDate(today);
};

/**
 *  Normalizar las fechas a medianoche (sin horas)
 * @param {String} date
 * @returns {Date}
 */
export const normalizeDate = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

/**
 * Adaptación del metodo getDay() para ambos idiomas
 * @param {Date} date
 */
export const getDateDay = (date) => {
  return (date.getDay() + 6) % 7;
};

/**
 * Añade/Resta días a una fecha
 * @param {Date} date
 * @param {Number} days
 */
export const addDays = (date, days) => {
  let new_date = parseDateAsUTC(date);
  new_date = new Date(new_date.setDate(new_date.getDate() + days));
  return new_date;
};

/**
 * Devuelve el inicio de semana de una fecha (lunes)
 * @param {Date} inputDate
 * @returns {Date}
 */
export const getStartOfWeek = (date) => {
  const dateTmp = parseDateAsUTC(date);
  const dayOfWeek = dateTmp.getDay();
  const diff = dateTmp.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
  // Hora actual
  let actualHour = parseDateAsUTC();
  actualHour = actualHour.getHours();
  const startDay = new Date(dateTmp.setDate(diff));
  startDay.setHours(0, 0, 0, 0);

  return startDay;
};

/**
 * Devuelve el final de la semana de una fecha (domingo)
 * @param {Date} date
 * @returns {Date}
 */
export const getEndOfWeek = (date) => {
  const endOfWeek = getStartOfWeek(date); // Crear una copia de la fecha de inicio de semana
  endOfWeek.setDate(endOfWeek.getDate() + 6); // Sumar 6 días para obtener el final de la semana
  // Comentado por error con fechas UTC server linux
  endOfWeek.setHours(23, 59, 59, 999); // Establecer la hora a 23:59:59
  return parseDateAsUTC(endOfWeek);
};

/**
 *  Cambia el formato de una fecha de MM/DD/YYYY a DD/MM/YYYY
 * @param {String} date - Fecha en formato MM/DD/YYYY
 * @param {String} separator - Separador entre los componentes de la fecha
 * @returns {String} - Fecha en formato DD/MM/YYYY
 */
export const changeFormatMDY = (date, separator = "/") => {
  const partes = date.split(separator);

  const month = partes[1];
  const day = partes[0];
  const year = partes[2];

  return month + separator + day + separator + year;
};

/** Cambia el formato de una fecha de DD/MM/YYYY a MM/DD/YYYY
 * @param {String} date - Fecha en formato DD/MM/YYYY
 * @param {String} separator - Separador entre los componentes de la fecha
 * @returns {String} - Fecha en formato MM/DD/YYYY
 **/
export const changeFormatYMD = (date, separator = "/") => {
  const partes = date.split(separator);

  const month = partes[1];
  const day = partes[0];
  const year = partes[2];

  return year + separator + month + separator + day;
};

/**
 * Comprueba si dos fechas están en el mismo año
 * @param {Date} date1 - Primera fecha
 * @param {Date} date2 - Segunda fecha
 * @returns {boolean} - Verdadero si ambas fechas están en el mismo año, falso en caso contrario
 */
export const sameYear = (date1, date2) => {
  const year1 = new Date(date1).getFullYear();
  const year2 = new Date(date2).getFullYear();

  return year1 === year2;
};

/** * Formatea una fecha a un formato específico (DD/MM/YYYY)
 * @param {Date} date - Fecha a formatear
 * @param {String} separator - Separador entre los componentes de la fecha
 * @returns {String} - Fecha formateada en el formato especificado
 */
export const getDateDMY = (date, separator = "/") => {
  let dateTmp = parseDateAsUTC(date);

  let day = dateTmp.getDate();
  let month = dateTmp.getMonth() + 1;
  const year = dateTmp.getFullYear();

  day = day < 10 ? "0" + day : day;
  month = month < 10 ? "0" + month : month;

  return day + separator + month + separator + year;
};

/**
 * Comprueba si una fecha es festivo
 * @param {Date} date - Fecha a comprobar
 * @return {boolean} - Verdadero si la fecha es festivo, falso en caso contrario
 */
export const isHoliday = (date) => {
  const calendar = useCalendar();
  const holidays = calendar.getHolidaysWeek;

  const fechaFormateada = getDateDMY(date);
  if (!holidays) return false;

  
  for (const holiday of holidays) {
    if (fechaFormateada === holiday.FESTIVO) {
      return true;
    }
  }

  return false;
};

/**
 * Comprueba si una fecha es menor que la fecha actual
 * @param {Date} date - Fecha a comprobar
 * @return {boolean} - Verdadero si la fecha es menor que la fecha actual, falso en caso contrario
 */
export const isMinorDate = (date) => {
  const today = parseDateAsUTC();
  return parseDateAsUTC(date) < today;
};

export const weekDayNames = [
  { es: "Lu", en: "Mon" },
  { es: "Ma", en: "Tue" },
  { es: "Mi", en: "Wed" },
  { es: "Ju", en: "Thu" },
  { es: "Vi", en: "Fri" },
  { es: "Sa", en: "Sat" },
  { es: "Do", en: "Sun" },
];

/**
 * Obtiene una lista de fechas entre dos fechas dadas, filtrando por días de la semana específicos
 * @param {Date} init - Fecha de inicio
 * @param {Date} end - Fecha de fin
 * @param {Array} weekDays - Array de días de la semana a incluir (0 = Domingo, 1 = Lunes, ..., 6 = Sábado)
 * @returns {Array} - Lista de fechas que cumplen con los criterios
 */
export const getDatesBetweenTwoDates = (init, end, weekDays) => {
  const datesList = [];
  let actualDate = parseDateAsUTC(init);

  while (actualDate < end) {
    if (weekDays.includes(getDateDay(actualDate))) {
      datesList.push(parseDateAsUTC(actualDate));
    }
    actualDate = addDays(actualDate, 1);
  }

  if (weekDays.includes(getDateDay(end))) {
    datesList.push(parseDateAsUTC(end));
  }

  return datesList;
};

/**
 * Comprueba si una fecha está dentro del rango de días de la semana actual
 * @param {Date} date - Fecha a comprobar
 * @returns {boolean} - Verdadero si la fecha está dentro del rango de días de la semana actual, falso en caso contrario
 */
export const getHolidaysBetweenDates = (dates) => {
  const masters = useMasters();
  const holidaysPerWeek = masters.getHolidaysPerWeek;
  const holidays = [];

  for (const date in dates) {
    const week = getWeek(dates[date]);
    const dateFormat = getDateDMY(dates[date], "/");
    // No guardo el formato nuevo para solo cambiarlo a la hora de mostrar
    const weekHolidays = holidaysPerWeek?.[week];
    if (!Array.isArray(weekHolidays)) continue;

    for (const holiday in weekHolidays) {
      if (dateFormat === weekHolidays[holiday].FESTIVO && !holidays.includes(dates[date])) {
        holidays.push(dates[date]);
      }
    }
  }

  return holidays;
};

/**
 * Obtiene el nombre de los festivos de una fecha y semana
 * @param {Date} date - Fecha a comprobar
 * @param {Number} week - Número de semana
 * @param {boolean} typeHtml - Si es true, devuelve el resultado en formato HTML
 * @returns {String} - Nombre de los festivos en formato HTML o texto plano
 */
export const getHolidaysName = (date, week, typeHtml = true) => {
  const nuxtApp = useNuxtApp();
  const masters = useMasters();
  const holidays = getHolidaysPerDay(date, week);
  const holidayKey = nuxtApp.$i18n.t("common.calendar.holiday");
  let notes = {};
  for (const holiday of holidays) {
    if (!notes.hasOwnProperty(holiday.NOTES) && holiday.NOTES !== null) {
      notes[holiday.NOTES] = [];
    }

    const country = masters.machines?.countries[holiday.CALENDARIO];
    const site = masters.machines?.sites[holiday.CALENDARIO];
    const holiday_location = country
      ? nuxtApp.$i18n.locale.value === "es"
        ? country.COUNTRYDESC_ES
        : country.COUNTRYDESC_EN
      : `${holiday.CALENDARIO} · ${site?.SITEDESC}`;

    if (holiday.NOTES) {
      notes[holiday.NOTES].push(holiday_location);
    } else {
      if (!notes.hasOwnProperty(holidayKey)) {
        notes[holidayKey] = [holiday_location];
      } else {
        notes[holidayKey].push(holiday_location);
      }
    }
  }
  if (typeHtml) {
    const result = Object.keys(notes).map(
      (note) => `<p><strong>${note}</strong></p> ${notes[note].join("<br/> ")}`
    );
    return result.length
      ? result.join("<br/><br/>")
      : nuxtApp.$i18n.t("common.calendar.holiday");
  } else {
    const result = Object.keys(notes).map((note) => `${note}`);
    return result.length ? result : nuxtApp.$i18n.t("common.calendar.holiday");
  }
};

/**
 * Obtiene los festivos de un día específico en una semana
 * @param {Date} date - Fecha a comprobar
 * @param {Number} week - Número de semana
 * @returns {Array} - Array de objetos con los festivos del día
 */
export const getHolidaysPerDay = (date, week) => {
  const masters = useMasters();
  const holidaysPerWeek = masters.getHolidaysPerWeek;
  const fechaFormateada = getDateDMY(date);
  const arrayHolidays = [];

  const weekHolidays = holidaysPerWeek?.[week];
  if (!Array.isArray(weekHolidays)) return arrayHolidays;

  for (const holiday of weekHolidays) {
    if (fechaFormateada === holiday.FESTIVO) {
      arrayHolidays.push(holiday);
    }
  }

  return arrayHolidays;
};

/**
 * Convierte horas en texto legible
 * @param {Number} hours - Número de horas a convertir
 * @param {Boolean} abbr -  Mostrar la abreviatura d ehoras
 * @returns {String} - Texto con el número de horas y minutos
 */
export const parseHoursToText = (hours, abbr = true) => {
  const { $i18n } = useNuxtApp();

  let horas = Math.floor(hours);
  let minutos = Math.round((hours - horas) * 60);
  if (minutos === 60) {
    horas += 1;
    minutos = 0;
  }
  if (horas === 0 && minutos === 0) {
    return `${horas}${abbr ? $i18n.t("common.fields.hours.abbr") : " "}`;
  }
  let result = `${horas}`;

  if (minutos > 0) {
    const minutosFormatted = minutos < 10 ? `0${minutos}` : minutos;
    result += `:${minutosFormatted}`;
  }

  result += abbr ? $i18n.t("common.fields.hours.abbr") : " ";

  return result;
};

/**
 * Comprueba si un día es laborable según la semana laboral
 * @param {String} laborableWeek - Representación de la semana laboral (ej. "7" para todos los días laborables, "6" para lunes a sábado, etc.)
 * @param {String} day - Día de la semana (0 = Domingo, 1 = Lunes, ..., 6 = Sábado)
 * @returns {boolean} - Verdadero si el día es laborable, falso en caso contrario
 */
export const isLaborableDay = (laborableWeek, day) => {
  if (laborableWeek == "7") return true;

  if (laborableWeek == "6" && day == "7") return false;

  if (laborableWeek == "5" && (day == "6" || day == "7")) return false;

  return true;
};

/**
 * Comprueba si dos fechas son el mismo día
 * @param {Date} date1 - Primera fecha
 * @param {Date} date2 - Segunda fecha
 * @returns {boolean} - Verdadero si ambas fechas son el mismo día, falso en caso contrario
 */
export const isSameDay = (date1, date2) => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

/**
 * Formatea una fecha a un formato específico (YYYY/MM/DD)
 * @param {Date} date - Fecha a formatear
 * @param {String} separator - Separador entre los componentes de la fecha
 * @returns {String} - Fecha formateada en el formato especificado (YYYY/MM/DD)
 */
export const getDateYMD = (date, separator = "/") => {
  let dateTmp = parseDateAsUTC(date);

  let day = dateTmp.getDate();
  let month = dateTmp.getMonth() + 1;
  const year = dateTmp.getFullYear();

  day = day < 10 ? "0" + day : day;
  month = month < 10 ? "0" + month : month;

  return year + separator + month + separator + day;
};

/**
 * Convierte una hora en formato HH:MM a un número decimal
 * @param {String} time - Hora en formato HH:MM
 * @return {String} - Hora en formato decimal (HH.MM) con dos decimales
 */
export const timeToDecimal = (time) => {
  const [hours, minutes] = time.split(":").map(Number);

  const decimalHours = hours + minutes / 60;

  return decimalHours.toFixed(2);
};

/**
 * Convierte un número decimal a una hora en formato HH:MM
 * @param {Number} decimal - Número decimal que representa horas (ej. 1.  25 para 1 hora y 15 minutos)
 * @return {String} - Hora en formato HH:MM (ej. "01:25")
 */
export const decimalToTime = (decimal) => {
  let hours = Math.floor(decimal);
  let minutes = Math.round((decimal - hours) * 60);
  if (minutes === 60) {
    hours += 1;
    minutes = 0;
  }

  const formattedHours = String(hours).padStart(2, "0");
  const formattedMinutes = String(minutes).padStart(2, "0");

  return `${formattedHours}:${formattedMinutes}`;
};

/**
 *  Compara una fecha con la fecha actual
 * * @param {Date} date - Fecha a comparar
 * @param {String} operator - Operador de comparación ('<', '<=', '==', '>=', '>')
 * @returns {boolean} - Resultado de la comparación
 * @throws {Error} - Si el operador no es válido
 */
export const compareDateWithToday = (date, operator) => {
  // Obtener la fecha actual
  const today = parseDateAsUTC();

  // Crear objetos de fecha con sólo año, mes y día para ambas fechas
  const d1 = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const d2 = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  // Comparar las fechas según el operador
  switch (operator) {
    case "<":
      return d1 < d2;
    case "<=":
      return d1 <= d2;
    case "==":
      return d1.getTime() === d2.getTime(); // comparar timestamps para igualdad exacta
    case ">=":
      return d1 >= d2;
    case ">":
      return d1 > d2;
    default:
      throw new Error("Operador inválido. Use <, <=, ==, >=, o >");
  }
};

/**
 * Comprueba si un día es fin de semana
 * @param {Date} date - Fecha a comprobar
 * @returns {boolean} - Verdadero si el día es fin de semana (sábado o domingo), falso en caso contrario
 */
export const isWeekend = (date) => {
  const day = date.getDay();
  return day === 0 || day === 6;
};

/**
 * Obtiene la fecha de la próxima semana
 * @returns {Date} - Fecha de la próxima semana (7 días después de la fecha actual)
 */
export const getNextWeek = () => {
  const actualDate = parseDateAsUTC();
  const nextWeekDate = parseDateAsUTC();
  nextWeekDate.setDate(actualDate.getDate() + 7);

  return nextWeekDate;
};

/**
 * Parses a date string and returns a JavaScript Date object, handling various formats and exceptions.
 *
 * @param {string|Date|null|undefined} dateStr - The date string to parse. If in 'YYYY-MM-DD' format, it is parsed as a local date.
 * @param {string} [treatAs=""] - Optional. Special treatment for certain formats (e.g., 'weird_formatting').
 * @param {string} [debug=""] - Optional. If provided, enables debug logging with the given label.
 * @returns {Date} The parsed Date object. If input is invalid or empty, returns the current date.
 */
export const parseDateAsUTC = (dateStr, treatAs = "", debug = "") => {
  let newDate = null;

  if (typeof dateStr === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split("-").map(Number);
    newDate = new Date(year, month - 1, day);
  } else if (typeof dateStr === "string" && /^\d{4}-\d{2}-\d{2}T/.test(dateStr)) {
    // ISO datetime con componente de tiempo (e.g. "2026-06-08T00:00:00Z")
    // Se extrae solo la parte de la fecha para evitar el desfase de zona horaria
    const [year, month, day] = dateStr.substring(0, 10).split("-").map(Number);
    newDate = new Date(year, month - 1, day);
  } else if (treatAs) {
    // Aplicar aquí excepciones especiales
    if (treatAs === "weird_formatting") {
      newDate = new Date();
    }
  } else if (!dateStr) {
    newDate = new Date();
  } else {
    newDate = new Date(dateStr);
  }

  if (debug) {
    console.log(
      `${debug ? "[" + debug + "] ---> " : ""}Fecha entrada: `,
      dateStr
    );
    console.log(
      `${debug ? "[" + debug + "] ---> " : ""}Fecha salida: `,
      newDate
    );
  }

  return newDate;
};

// composables/dates.js
// composables/dates.js
export function useWeekRange() {
  const nuxtApp = useNuxtApp();
  const calendar = useCalendar();

  /**
   * Obtiene la etiqueta de la semana (Meses/Año y número de semana).
   * @param {Date | string} date - La fecha a partir de la cual calcular la semana.
   * @param {boolean} abbr - Si se deben usar meses abreviados.
   * @returns {string} La etiqueta de la semana formateada.
   */
  const getMonthWeekData = (date = calendar.selectedDate, abbr = false) => {
    if (!date) return "";

    // Convertir la fecha de entrada a un objeto Date en UTC (asumiendo que parseDateAsUTC lo hace)
    const d = parseDateAsUTC(date);

    // 1. Cálculo de la semana (Lunes y Domingo)
    const dayOfWeek = d.getDay(); // 0 = Domingo, 1 = Lunes, ..., 6 = Sábado

    // Calcular Lunes: Restar días desde el día actual. Si es domingo (0), restar 6.
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const monday = parseDateAsUTC(d); // Crear una copia mutable
    monday.setDate(d.getDate() - daysToSubtract);

    // Calcular Domingo: 6 días después del lunes
    const sunday = parseDateAsUTC(monday); // Crear copia de Lunes
    sunday.setDate(monday.getDate() + 6);

    // 2. Etiqueta del número de semana (Si `getWeek` está disponible)
    let weekLabel = "";
    if (typeof getWeek === "function") {
      const weekNumber = getWeek(monday);
      weekLabel = weekNumber
        ? ` / ${nuxtApp.$i18n.t("common.calendar.week.abrev")}: ${weekNumber}`
        : "";
    }

    // 3. Helper para formatear el mes (abreviado o completo)
    const formatMonth = (dateObj) => {
      // Usamos el formato 'monthName' (completo) y luego abreviamos si es necesario.
      const fullMonth = formatDate(dateObj, "monthName");
      return abbr ? fullMonth.slice(0, 3) : fullMonth;
    };

    const monthMonday = formatMonth(monday);
    const monthSunday = formatMonth(sunday);
    const yearMonday = formatDate(monday, "year");
    const yearSunday = formatDate(sunday, "year");

    // 4. Lógica de formateo condicional
    let dateRange;

    // Caso A: La semana cruza el cambio de año (Ej: Dic 2025 -> Ene 2026)
    if (yearMonday !== yearSunday) {
      dateRange = `${monthMonday} ${yearMonday} - ${monthSunday} ${yearSunday}`;
    }
    // Caso B: La semana cruza el cambio de mes (Ej: Ene 25 -> Feb 02)
    else if (monday.getMonth() !== sunday.getMonth()) {
      dateRange = `${monthMonday} - ${monthSunday} ${yearSunday}`;
    }
    // Caso C: La semana está completamente dentro del mismo mes
    else {
      dateRange = `${monthMonday} ${yearSunday}`;
    }

    return `${dateRange}${weekLabel}`;
  };

  return { getMonthWeekData };
}
